/**
 * Programmatically fetch high-quality images from Google Custom Search
 * Ensures authentic visuals for Yoga Aid poses and Nutri Heal recipes.
 */
const KNOWN_LOCAL_FILES: string[] = [
  "ABC-Stretch.png",
  "adhomukhshvanasana.jpg",
  "anuloma-viloma.jpg",
  "ardha-matsyendrasana.jpg",
  "arghyam.jpg",
  "badhkonsasana.jpg",
  "balasana.jpg",
  "barefoot-dew-walking.jpg",
  "barefoot-grounding.jpg",
  "bhastrika.jpg",
  "bhramari.png",
  "bhujangasana.jpg",
  "brisk-nature-walk.png",
  "cat-cow.jpg",
  "chest-expansion--modified-fish-pose-.jpg",
  "chest-opening-stretch.jpg",
  "dhanurasana.jpg",
  "dirga-pranayam.png",
  "equal-breathing.png",
  "evening-moon-walk.jpg",
  "gentle-nature-walk.jpg",
  "gomukhasana-arms.jpg",
  "kapalbhati.jpg",
  "leg-ankle-awakening.jpg",
  "malasana.jpg",
  "mindful-grounding-walk.jpg",
  "nature-kapha.png",
  "nature-pitta.png",
  "nature-vata.png",
  "neck-palpation-stretch.jpg",
  "neck-shoulder-release.jpg",
  "paschimottanasana.jpg",
  "pavanamuktasana.jpg",
  "post-meal-walk.jpg",
  "setubandhasana.jpg",
  "shavasana.jpg",
  "sheetali.jpg",
  "slow-mindful-walk.jpg",
  "sukhasana-with-neck-rolls.jpg",
  "sukhasana.jpg",
  "sun-salutation.png",
  "supta-matsyendrasana.jpg",
  "surya-darshan.jpg",
  "surya-namaskar.jpg",
  "tadasana.jpg",
  "trataka.jpg",
  "trikonasana.jpg",
  "uttanasana.jpg",
  "vajrasana.jpg",
  "viparita-karani.jpg",
  "virabhadrasana-1.jpg",
  "virabhadrasana-2.jpg",
  "virasana.jpg",
  "vrikshasana.jpg"
];

// If local diet library exists, add known files here to bypass dynamic disk checks
const KNOWN_LOCAL_DIET_FILES: string[] = [];

const EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
import { checkCloudImageCache } from './backendService';

function getSanskritVariations(name: string): { variants: string[], matchType: 'exact' | 'prefix' | 'value' | null } {
  const dictionary: { [key: string]: string[] } = {
    "virabhadrasana-2": ["virabhadrasana ii", "warrior 2"],
    "tree pose": ["vrksasana"],
    "downward-facing dog": ["adho mukha svanasana", "downward dog", "adhomukhshvanasana", "adho mukh shvanasana"],
    "cobra pose": ["bhujangasana"],
    "triangle pose": ["trikonasana", "utthita trikonasana"],
    "child's pose": ["balasana", "childs pose"],
    "corpse pose": ["savasana", "shavasana"],
    "cat-cow": ["marjaryasana", "bitilasana", "cat cow", "marjaryasana-bitilasana", "cat-cow-stretch", "cat-cow-pose"],
    "seated forward bend": ["paschimottanasana", "forward-bend-pose"],
    "bridge pose": ["setu bandhasana"],
    "easy pose": ["sukhasana"],
    "legs up wall": ["viparita karani", "legs-up-the-wall", "legs-up-wall-pose"],
    "butterfly pose": ["baddha konasana"],
    "mountain pose": ["tadasana", "mountain-pose", "mountain pose"],
    "equal breathing": ["sama vritti"],
    "alternate nostril breathing": ["nadi shodhana", "anuloma viloma", "anuloma-viloma"],
    "belly breathing": ["diaphragmatic breathing", "belly-breathing", "abdominal breathing"],
    "thunderbolt pose": ["vajrasana"],
    "seated spinal twist": ["matsyendrasana", "ardha-matsyendrasana"],
    "supine spinal twist": ["supta matsyendrasana"],
    "dirga pranayam": ["three part breath"],
    "hero's pose": ["virasana", "hero pose"],
    "standing forward bend": ["uttanasana", "forward fold", "uttanasana-zen"],
    "bhramari": ["bee breath", "bhramari-pranayama"],
    "sheetali": ["cooling breath", "sheetali-pranayama", "shitali", "sitali", "sitali-pranayama", "shitali-pranayama"],
    "half lord of the fishes": ["ardha matsyendrasana"],
    "pavanamuktasana": ["apanasana", "wind relieving pose", "wind-relieving pose", "pavan-muktasana", "pavan muktasana"],
    "skull shining breath": ["kapalabhati", "kapalabhati pranayama"],
    "shoulder stand": ["sarvangasana", "inversion"],
    "bow pose": ["dhanurasana"],
    "brisk nature walk": ["nature-walk-in-winter", "brisk nature walk"],
    "nature walk": ["mindful nature walk", "nature-walk"],
    "sun salutation": ["surya namaskar", "sun-salutation", "suyra namaskar", "surya-namaskar", "surya-namaskar--sun-salutations-", "Surya Namaskar (Sun Salutation)"],
    "abc stretch": ["abc stretch (arm, back, chest)", "abc-stretch", "abc stretch arm, back, chest", "ABC Stretch (Arm, Back, Chest Expansion)"],
    "trataka": ["trataka", "trataka gazing", "candle gazing", "fixed gazing meditation", "trataka eye exercise"],
    "anuloma viloma": ["anuloma viloma", "alternate nostril breathing", "nadi shodhana", "anuloma-viloma"],
    "sukhasana with neck rolls": ["sukhasana-with-neck-rolls", "neck rolls"],
    "mindful grounding walk": ["grounding walk", "mindful-grounding-walk"],
    "barefoot grounding": ["bhumi sparsha", "barefoot-grounding"],
    "barefoot dew walking": ["dew walking", "barefoot-dew-walking"],
    "gentle nature walk": ["gentle-nature-walk"]
  };

  const nameLower = name.toLowerCase().trim();

  for (const [key, variants] of Object.entries(dictionary)) {
    // Exact match
    if (nameLower === key || variants.some(v => v.toLowerCase() === nameLower)) {
      return { variants: [key, ...variants], matchType: 'exact' };
    }
    // Prefix match
    if (nameLower.startsWith(key) || variants.some(v => nameLower.startsWith(v.toLowerCase()))) {
      return { variants: [key, ...variants], matchType: 'prefix' };
    }
    // Substring/Value match
    if (nameLower.includes(key) || variants.some(v => nameLower.includes(v.toLowerCase()))) {
      return { variants: [key, ...variants], matchType: 'value' };
    }
  }

  // Aggressive Normalization for "Nature Walk"
  const isWalking = nameLower.includes('walk') || nameLower.includes('hike') || nameLower.includes('forest') || nameLower.includes('nature');
  if (isWalking && !nameLower.includes('postural')) {
    return { variants: ['nature walk', 'nature-walk', 'mindful nature walk', 'brisk nature walk'], matchType: 'value' };
  }

  return { variants: [], matchType: null };
}

export function findLocalFuzzyMatch(poseName: string): string | null {
  const baseName = poseName.toLowerCase().trim();
  const { variants, matchType } = getSanskritVariations(baseName);

  if (matchType) {
    logger.log(`[YogaAid Dict] Match found for "${poseName}" | type: ${matchType} | using: ${variants[0]}`);
  } else {
    logger.log(`[YogaAid Dict] Dictionary MISS for "${poseName}"`);
  }

  const variations = new Set<string>();

  const addVariation = (v: string) => {
    const clean = v.toLowerCase().trim();
    variations.add(clean);
    variations.add(clean.replace(/\s+/g, '-'));
    variations.add(clean.replace(/\s+/g, '_'));
    // Handle names with parentheses: "Surya Namaskar (Sun Salutation)"
    if (clean.includes('(')) {
      const parentheticalStripped = clean.replace(/\(.*?\)/g, '').trim();
      if (parentheticalStripped) {
        variations.add(parentheticalStripped);
        variations.add(parentheticalStripped.replace(/\s+/g, '-'));
      }
    }
    // Handle and/or combinations
    if (clean.includes('/')) {
      const parts = clean.split('/');
      parts.forEach(p => variations.add(p.trim().replace(/\s+/g, '-')));
    }
  };

  addVariation(baseName);
  variants.forEach(v => addVariation(v));
  variations.add(baseName.split(/\s+/)[0]);

  for (const variation of variations) {
    for (const ext of EXTENSIONS) {
      const fileName = `${variation}${ext}`;
      if (KNOWN_LOCAL_FILES.includes(fileName)) {
        return `/images/yoga/${fileName}`;
      }
    }
  }

  // Also try partial match against KNOWN_LOCAL_FILES for resilience
  for (const file of KNOWN_LOCAL_FILES) {
    if (file.includes(baseName.replace(/\s+/g, '-'))) {
      return `/images/yoga/${file}`;
    }
  }

  return null;
}

function findLocalMealFuzzyMatch(mealName: string): string | null {
  const baseName = mealName.toLowerCase().trim();
  const variations = [
    baseName,
    baseName.replace(/\s+/g, '-'),
    baseName.replace(/\s+/g, '_')
  ];

  for (const variation of variations) {
    for (const ext of EXTENSIONS) {
      const fileName = `${variation}${ext}`;
      if (KNOWN_LOCAL_DIET_FILES.includes(fileName)) {
        return `/images/diet/${fileName}`;
      }
    }
  }
  return null;
}

import { ImageResult } from '../types';
import { generateAndSaveImage } from './imageGenerationService';
import logger from "@/utils/logger";

export const CONFIDENCE_THRESHOLD = { pose: 55, meal: 35 };

const imageCache = new Map<string, ImageResult>();

const POSES_REQUIRING_GENERATION = new Set([
  'gentle-nature-walk',
  'mindful-grounding-walk', 
  'brisk-nature-walk',
  'slow-mindful-walk',
  'barefoot-grounding',
  'barefoot-dew-walking',
  'evening-moon-walk',
  'post-meal-walk',
  'arghyam',
  'surya-darshan',
  'trataka'
]);

const TRUSTED_YOGA_DOMAINS = [
  // Yoga Pose Image Sources
  'yogajournal.com', 'yogapedia.com', 'ekhartyoga.com', 'doyogawithme.com', 'verywellfit.com/yoga',
  'verywellfit.com', 'yogauonline.com', 'unsplash.com', 'pexels.com', 'pixabay.com'
];

// Food & Recipe Sources + Free Food Stock Photography
const TRUSTED_FOOD_DOMAINS = [
  'allrecipes.com', 'healthline.com', 'eatingwell.com',
  'skinnytaste.com', 'downshiftology.com', 'minimalistbaker.com',
  'bbcgoodfood.com', 'thekitchn.com', 'simplyrecipes.com',
  'budgetbytes.com', 'fitmencook.com', 'healthyfitnessmeals.com',
  'cookinglight.com', 'halfbakedharvest.com',
  'foodiesfeed.com', 'feastingathome.com', 'loveandlemons.com',
  'cookieandkate.com', '101cookbooks.com', 'unsplash.com', 'pexels.com',
  // Ayurvedic and Indian Recipe Sources
  'archanaskitchen.com', 'vegrecipesofindia.com', 'indianhealthyrecipes.com',
  'kannammacooks.com', 'ministryofcurry.com', 'theayurvedaexperience.com', 'ayurveda.com'
];
const PENALIZED_DOMAINS = ['shutterstock', 'getty', 'pinterest', 'istockphoto', 'dreamstime', '123rf'];

export const detectDishFormat = (mealName: string): string => {
  const name = mealName.toLowerCase();

  // Grain and porridge checks MUST come first — before beverage check
  if (/oatmeal|porridge|oats/.test(name)) return 'oatmeal porridge bowl cooked';
  if (/upma/.test(name)) return 'Indian upma semolina dish bowl';
  if (/khichdi|khichri|kitchari/.test(name)) return 'Indian khichdi rice lentil bowl';

  // Soup and broth next
  if (/broth/.test(name)) return 'clear broth soup bowl served hot';
  if (/soup/.test(name)) return 'soup bowl served hot';
  if (/stew/.test(name)) return 'stew bowl served';

  // Cooked vegetable dishes
  if (/sauté|saute|stir.?fry/.test(name)) return 'sautéed cooked vegetables pan dish';
  if (/steamed/.test(name)) return 'steamed vegetables plate dish';
  if (/roasted/.test(name)) return 'roasted vegetables plate dish';

  // Salads
  if (/salad|slaw/.test(name)) return 'salad bowl fresh';

  // Breakfast bowls
  if (/pudding|chia/.test(name)) return 'pudding dessert bowl';
  if (/bowl/.test(name)) return 'grain bowl served';

  // Baked and stewed fruit
  if (/stewed|baked/.test(name) && /apple|pear|fruit/.test(name)) {
    return 'stewed fruit bowl dessert';
  }

  // Beverages LAST — only if nothing else matched
  if (/tonic|juice|smoothie|drink|milk|tea/.test(name)) return 'drink glass served';
  if (/broth/.test(name)) return 'broth soup bowl'; // second broth catch

  return 'plated dish served bowl';
};

export const normaliseMealName = (mealName: string): string => {
  let normalised = mealName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (normalised.includes('saute')) {
    normalised = normalised.replace(/saute/g, 'saute sautéed pan cooked');
  }
  return normalised;
};

const scoreResult = (item: any, mealName: string, trustedDomains: string[]): number => {
  const title = (item.title || '').toLowerCase();
  const snippet = (item.snippet || '').toLowerCase();
  const link = (item.link || '').toLowerCase();
  const nameLower = mealName.toLowerCase();

  // Step 4A: Add nature/landscape illustration detection to CSE scorer
  const landscapeIllustrationTerms = [
    'watercolor', 'watercolour', 'painting', 'illustration', 
    'artwork', 'drawing', 'sketch', 'vector', 'clipart',
    'art print', 'poster', 'forest illustration', 'nature art'
  ];

  if (landscapeIllustrationTerms.some(term =>
    title.includes(term) ||
    link.includes(term) ||
    snippet.includes(term)
  )) {
    return 0; // hard zero — never accept illustrated landscapes for nature walks
  }

  let score = 0;

  const expectedFormat = detectDishFormat(mealName);
  if (expectedFormat) {
    if (title.includes(expectedFormat)) {
      score += 20;
    }

    // Define hard clashes to avoid over-penalizing synonyms
    const isLiquid = /soup|stew|broth|dal|khichdi|drink|juice|smoothie/i.test(expectedFormat);
    const isSolid = /saute|salad|toast|wrap|bread|muffin|cake|cookie|roll/i.test(expectedFormat);

    if (isLiquid && /bread|muffin|cake|cookie|toast/i.test(title)) {
      score -= 25;
    } else if (isSolid && /soup|stew|broth|drink/i.test(title)) {
      score -= 25;
    }
  }

  // Soft gate: match ratio against the original meal name
  const meaningfulWords = nameLower.split(' ').filter(w => w.length > 3 && w !== 'recipe');
  if (meaningfulWords.length > 0) {
    let matchCount = 0;
    meaningfulWords.forEach(w => {
      if (title.includes(w)) matchCount++;
    });
    const matchRatio = matchCount / meaningfulWords.length;
    if (matchRatio < 0.4) {
      score -= 15; // Soft penalty instead of hard cap
    }
  }

  // 1. Exact Name match in title (+40)
  if (title.includes(nameLower)) score += 40;

  // 2. Individual words match in title (+10 each)
  meaningfulWords.forEach(word => {
    if (title.includes(word)) score += 10;
  });

  // 3. Contextual keywords (+5 each)
  const contextWords = ['yoga', 'pose', 'asana', 'recipe', 'meal', 'food', 'healthy', 'nutrition'];
  contextWords.forEach(word => {
    if (title.includes(word) || snippet.includes(word)) score += 5;
  });

  // 4. Trusted domain bonus
  if (trustedDomains.some(domain => link.includes(domain))) {
    score += 15;
  }

  // 5. Penalized domains (-30)
  if (PENALIZED_DOMAINS.some(domain => link.includes(domain))) score -= 30;

  return Math.min(100, Math.max(0, score));
};

const executeSearch = async (query: string, category: 'yoga' | 'food'): Promise<ImageResult> => {
  try {
    logger.log(`[ImagePipeline] Querying Google: "${query}"`);
    const response = await fetch('/api/google-search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      logger.error(`[ImagePipeline] Proxy returned error ${response.status}:`, await response.text());
      return { url: null, source: 'none', confidence: 0 };
    }

    const data = await response.json();

    if (data.items && data.items.length > 0) {
      const trustedDomains = category === 'yoga' ? TRUSTED_YOGA_DOMAINS : TRUSTED_FOOD_DOMAINS;

      let bestResult: ImageResult = { url: null, source: 'none', confidence: 0 };

      for (const item of data.items) {
        let imageUrl = item.link;
        if (imageUrl.startsWith('http://')) {
          imageUrl = imageUrl.replace('http://', 'https://');
        }

        let cleanName = query.split('site:')[0].trim().replace(/"/g, '');
        // For fallback tracking, ensure meal query words are passed
        if (category === 'food') {
          cleanName = cleanName.replace('healthy recipe photo', '').replace('recipe', '').trim();
        }
        const score = scoreResult(item, cleanName, trustedDomains);

        if (score > bestResult.confidence) {
          bestResult = {
            url: imageUrl,
            source: new URL(imageUrl).hostname,
            confidence: score
          };
        }
      }
      return bestResult;
    }
  } catch (error) {
    logger.error(`[ImagePipeline] Network error during image fetch for ${query}:`, error);
  }
  return { url: null, source: 'none', confidence: 0 };
};

export const getCachedPoseImage = async (poseName: string, practiceDescription?: string, vectorDescription?: string): Promise<ImageResult> => {
  const originalQuery = poseName.toLowerCase().trim();

  // Aggressive Stripper: Remove common LLM adjectives
  const adjectivesToRemove = ['controlled', 'mindful', 'gentle', 'brisk', 'soothing', 'deep', 'slow', 'retention'];
  const regex = new RegExp(`\\b(?:${adjectivesToRemove.join('|')})\\b`, 'gi');
  let strippedQuery = originalQuery.replace(regex, '').replace(/\s+/g, ' ').trim();

  // Keyword check for belly breathing / relaxation
  if (strippedQuery.includes('belly') || strippedQuery.includes('abdominal') || strippedQuery.includes('diaphragmatic') || strippedQuery.includes('progressive relaxation') || strippedQuery.includes('progression relaxation')) {
    strippedQuery = 'belly breathing';
  }

  const normalizedQuery = strippedQuery;

  // Pre-emptive forced override: If it's a "walk" of any kind, map straight to "nature walk"
  // This circumvents Google Search entirely for these AI-generated variants
  const isWalking = normalizedQuery.includes('walk') || normalizedQuery.includes('hike') || normalizedQuery.includes('forest') || normalizedQuery.includes('nature');
  let finalQuery = normalizedQuery;

  if (isWalking) {
    finalQuery = "nature walk";
  }

  // Step 4B: Add nature walk poses to isAyurvedicMeal() equivalent for poses — force DALL-E for nature integration poses
  // Nature walk poses will never be found correctly on yoga pose sites. Add them to a requiresGeneration list that skips Google CSE entirely:
  const strippedName = poseName.replace(/\(.*?\)/g, '').trim();
  const poseKey = strippedName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  
  if (POSES_REQUIRING_GENERATION.has(poseKey)) {
    console.log(`[ImagePipeline] POSE "${poseName}" → Nature integration pose — skipping CSE, using DALL-E or local`);
    // Check local first, then DALL-E — skip Google CSE entirely
    const localPath = findLocalFuzzyMatch(poseName);
    if (localPath) {
      const res: ImageResult = { url: localPath, source: 'local', confidence: 100 };
      imageCache.set(finalQuery, res);
      return res;
    }
    return await generateAndSaveImage(poseName, practiceDescription || '', 'pose', vectorDescription);
  }

  if (imageCache.has(finalQuery)) {
    logger.log(`[ImagePipeline] cache hit for pose: ${finalQuery}`);
    return imageCache.get(finalQuery)!;
  }

  // ==== STEP 1: STRICT LOCAL CHECK ====
  const localMatch = findLocalFuzzyMatch(finalQuery);
  if (localMatch) {
    logger.log(`[ImagePipeline] strict local library hit for pose: ${finalQuery} (${localMatch})`);
    const res: ImageResult = { url: localMatch, source: 'local', confidence: 100 };
    imageCache.set(finalQuery, res);
    return res;
  }

  // ==== STEP 1.5: CLOUD CACHE CHECK ====
  const filename = `${finalQuery.replace(/[^a-z0-9]/g, '-')}.jpg`;
  const cloudMatch = await checkCloudImageCache(filename, 'yoga');
  if (cloudMatch) {
    logger.log(`[ImagePipeline] Cloud Cache hit for pose: ${finalQuery} (${cloudMatch})`);
    const res: ImageResult = { url: cloudMatch, source: 'cloud-cache', confidence: 100 };
    imageCache.set(finalQuery, res);
    return res;
  }


  if (process.env.NODE_ENV === 'development') {
    logger.log(`[YogaAid ImageAudit] No local image found for: "${normalizedQuery}"`);
  }

  // ==== STEP 2: GOOGLE CSE ====
  const isWalkingPose = finalQuery.includes('walk') || finalQuery.includes('hike');
  const isBreathing = finalQuery.includes('breath') || finalQuery.includes('pranayama');

  let primaryQuery = '';
  if (isWalkingPose) {
    primaryQuery = `"${normalizedQuery}" wellness nature outdoors ${TRUSTED_YOGA_DOMAINS.map(d => `site:${d}`).join(' OR ')}`;
  } else if (isBreathing) {
    primaryQuery = `"${normalizedQuery}" seated breathing practice ${TRUSTED_YOGA_DOMAINS.map(d => `site:${d}`).join(' OR ')}`;
  } else {
    primaryQuery = `"${normalizedQuery}" yoga pose ${TRUSTED_YOGA_DOMAINS.map(d => `site:${d}`).join(' OR ')}`;
  }

  let result = await executeSearch(primaryQuery, 'yoga');

  if (result.confidence < CONFIDENCE_THRESHOLD.pose) {
    logger.log(`[ImagePipeline] Google search score too low (${result.confidence}) for primary query, trying fallback: ${normalizedQuery}`);
    const fallbackQuery = `${normalizedQuery} yoga how to do`;
    const fallbackResult = await executeSearch(fallbackQuery, 'yoga');
    if (fallbackResult.confidence > result.confidence) {
      result = fallbackResult;
    }
  }

  if (result.confidence >= CONFIDENCE_THRESHOLD.pose) {
    logger.log(`[ImagePipeline] Google search confident match (${result.confidence}) for pose: ${normalizedQuery}`);
    imageCache.set(normalizedQuery, result);
    return result;
  }

  // ==== STEP 3: AI GENERATION ====
  logger.log(`[ImagePipeline] Google search failed to reach threshold for pose: ${normalizedQuery}. Triggering AI generation.`);
  const aiResult = await generateAndSaveImage(
    normalizedQuery,
    practiceDescription || '',
    'pose',
    vectorDescription
  );

  if (aiResult.url && aiResult.source !== 'error' && aiResult.source !== 'timeout') {
    // Will naturally map the new image into the local cache map
    imageCache.set(normalizedQuery, aiResult);
    return aiResult;
  }

  // ==== STEP 4: PLACEHOLDER FALLBACK ====
  logger.log(`[ImagePipeline] All search and generation strategies exhausted for pose: ${normalizedQuery}`);
  return { url: null, source: 'none', confidence: 0 };
};

export const buildMealQuery = (mealName: string): string => {
  const format = detectDishFormat(mealName);

  const nameKeywords = normaliseMealName(mealName)
    .toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 2)
    .join(' ');

  const query = `${nameKeywords} ${format} recipe`;
  console.log(`[ImagePipeline] Google CSE query for "${mealName}": "${query}"`);
  return query;
};

export const getCachedMealImage = async (mealQuery: string, benefitDescription: string, vectorDescription?: string): Promise<ImageResult> => {
  const normalizedQuery = mealQuery.toLowerCase().trim();

  if (imageCache.has(normalizedQuery)) {
    logger.log(`[ImagePipeline] cache hit for meal: ${normalizedQuery}`);
    return imageCache.get(normalizedQuery)!;
  }

  // ==== STEP 1: STRICT LOCAL CHECK ====
  const localMatch = findLocalMealFuzzyMatch(normalizedQuery);
  if (localMatch) {
    logger.log(`[ImagePipeline] strict local library hit for meal: ${normalizedQuery} (${localMatch})`);
    const res: ImageResult = { url: localMatch, source: 'local', confidence: 100 };
    imageCache.set(normalizedQuery, res);
    return res;
  }

  // ==== STEP 1.5: CLOUD CACHE CHECK ====
  const filename = `${normalizedQuery.replace(/[^a-z0-9]/g, '-')}.jpg`;
  const cloudMatch = await checkCloudImageCache(filename, 'diet');
  if (cloudMatch) {
    logger.log(`[ImagePipeline] Cloud Cache hit for meal: ${normalizedQuery} (${cloudMatch})`);
    const res: ImageResult = { url: cloudMatch, source: 'cloud-cache', confidence: 100 };
    imageCache.set(normalizedQuery, res);
    return res;
  }


  // ==== STEP 2: GOOGLE CSE ====
  const primarySearchStr = buildMealQuery(normalizedQuery);
  let result = await executeSearch(primarySearchStr, 'food');

  if (result.confidence < CONFIDENCE_THRESHOLD.meal) {
    logger.log(`[ImagePipeline] Google search score too low (${result.confidence}) for primary query, trying fallback: ${normalizedQuery}`);
    // Fallback query without repeating words
    const fallbackWords = [...new Set(`${normalizedQuery} healthy recipe photo`.split(' '))];
    const fallbackQuery = fallbackWords.join(' ');

    const fallbackResult = await executeSearch(fallbackQuery, 'food');
    if (fallbackResult.confidence > result.confidence) {
      result = fallbackResult;
    }
  }

  if (result.confidence >= CONFIDENCE_THRESHOLD.meal) {
    logger.log(`[ImagePipeline] Google search confident match (${result.confidence}) for meal: ${normalizedQuery}`);
    imageCache.set(normalizedQuery, result);
    return result;
  }

  // ==== STEP 3: AI GENERATION ====
  logger.log(`[ImagePipeline] MEAL "${normalizedQuery}" → Google CSE MISS — triggering DALL-E generation.`);
  const aiResult = await generateAndSaveImage(
    normalizedQuery,
    benefitDescription,
    'meal',
    vectorDescription
  );

  if (aiResult.url && aiResult.source !== 'error' && aiResult.source !== 'timeout') {
    imageCache.set(normalizedQuery, aiResult);
    return aiResult;
  }

  // ==== STEP 4: PLACEHOLDER FALLBACK ====
  logger.log(`[ImagePipeline] All search and generation strategies exhausted for meal: ${normalizedQuery}`);
  return { url: null, source: 'none', confidence: 0 };
};

// Kept for backward compatibility if needed, but components should migrate
export const fetchImageFromSearch = async (query: string, category: 'yoga' | 'food'): Promise<string | undefined> => {
  if (category === 'yoga') {
    const res = await getCachedPoseImage(query, '', '');
    return res.url || undefined;
  } else {
    const res = await getCachedMealImage(query, '');
    return res.url || undefined;
  }
};

export const testMealImageLookup = async () => {
  if (process.env.NODE_ENV !== 'development') return;

  const testMeals = [
    "Spinach Mushroom Omelette",
    "Salmon Quinoa Bowl",
    "Chia Seed Pudding Berries",
    "Turkey Avocado Wrap",
    "Greek Yogurt Parfait"
  ];

  logger.log("=== Running Meal Image Lookup Smoke Test ===");
  for (const meal of testMeals) {
    const result = await getCachedMealImage(meal, 'mock benefit');
    logger.log(`[ImagePipeline] "${meal}" → source: ${result.source} | confidence: ${result.confidence} | url: ${result.url}`);
  }
  logger.log("=== Smoke Test Complete ===");
};